import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { Button, Checkbox, FormControlLabel, Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { FormikValues, useFormik } from "formik";
import { useEffect, useMemo, useRef, useState } from "react";
import CustomSelect from "../../../../../components/CustomSelect";
import { CustomSelectScroll } from "../../../../../components/CustomSelectScroll";
import CustomTextArea from "../../../../../components/CustomTextArea";
import Flex from "../../../../../components/Flex";
import Input from "../../../../../components/Input";
import MaskMultiSelect from "../../../../../components/MaskMultiSelect";
import MaskMultiSelectScroll from "../../../../../components/MaskMultiSelectScroll";
import MultiSelect from "../../../../../components/MultiSelect";
import NumberInput from "../../../../../components/NumberInput";
import ProfileViewer from "../../../../../components/ProfileViewer";
import SideSheetDrawer from "../../../../../components/SidesheetDrawer";
import Text from "../../../../../components/Text";
import TimeSlotPicker from "../../../../../components/TimeSlotPicker";
import {
  CustomFrequencyUnit,
  EndType,
} from "../../../../../components/app/provider/scheduling/RecurringConfig";
import {
  NEW_APPOINTMENT_VISIT_TYPE_MAPPER,
  RESCHEDULE_APPOINTMENT_SCHEMA,
} from "../../../../../constants/scheduling";
import { Status } from "../../../../../dummy-data/appointment-list-data-scheduling";
import { useDebounce } from "../../../../../hooks/use-debounce";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import { useGetAllScreenerType } from "../../../../../queries/patient-chart/assessment/assement-forms/use-get-all-screener-type";
import { useLocationsList } from "../../../../../queries/use-locationList";
import { useRescheduleAppointment } from "../../../../../queries/use-reschedule-appointment";
import {
  formatDateTime,
  formatTime,
} from "../../../../../utils/date-time-utility";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  getErrorSnackbar,
  getSuccessSnackbar,
  getXtenantId,
  replaceUnderScoreAddSpace,
} from "../../../../../utils/utils";
import { APPOINTMENT_STATUS_RECORD } from "./AppointmentScheduling";
import {
  INTAKE_FORM_SECTIONS,
  INTAKE_FORM_SECTIONS_MAPPER,
} from "./appointment-constant";
import { getStoredRealm } from "@/utils/auth-storage";

interface NewAppointmentType extends FormikValues {
  patientId: string;
  visit_type: string;
  type: string;
  locationId: string;
  providerSpecialities?: object;
  providerId: string;
  date_and_time: string;
  room: string;
  // reason_for_appointment: string;
  chiefComplaint: string;
  note: string;
  primary_insurance: string;
  secondary_insurance: string;
  authorization: string;
  forms: string[];

  reminder_set: boolean;

  endType: EndType;
  endDate: Date;
  endAfter: number;

  customFrequency: number;
  customFrequencyUnit: CustomFrequencyUnit;
  selectedWeekdays: number[];
  reminder_before_number: number;
}

const NEW_APPOINTMENT_INITIAL_VALUES: NewAppointmentType = {
  mode: "IN_PERSON",
  patientId: "",
  visit_type: "",
  type: "",
  locationId: "",
  providerSpecialities: undefined,
  providerId: "",
  date_and_time: "",
  room: "",
  // reason_for_appointment: "",
  chiefComplaint: "",
  note: "",
  primary_insurance: "",
  secondary_insurance: "",
  authorization: "",
  forms: [],

  reminder_set: false,

  endType: "never",
  endDate: new Date(),
  endAfter: 5,

  customFrequency: 1,
  customFrequencyUnit: "days",
  selectedWeekdays: [],
  reminder_before_number: 1,
};

interface NewAppointmentProps {
  opened: boolean;
  onClose: () => void;
  appointmentData?: any;
  successHandler: (message: string) => void;
}

const RescheduleAppointment = ({
  opened,
  onClose,
  appointmentData,
  successHandler,
}: NewAppointmentProps) => {
  const [locationSearch, setLocationSearch] = useDebounce<string>("");
  const [locationPagination, setLocationPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const { data: providerGroupLocationList, isFetching: isLocationFetching } =
    useLocationsList({
      pagination: locationPagination,
      searchKey: locationSearch,
      providerGroupId: getStoredRealm() ?? "",
    });
  // const { data: customForms }: any = useCustomFormList({
  //   pagination: DEF_PAGINATION,
  //   searchKey: "",
  //   providerGroupId: getStoredRealm() ?? undefined,
  // });

  const { mutateAsync: rescheduleAppointment } = useRescheduleAppointment();
  const client = useQueryClient();

  const formik = useFormik({
    initialValues: NEW_APPOINTMENT_INITIAL_VALUES,
    validationSchema: RESCHEDULE_APPOINTMENT_SCHEMA,
    onSubmit: handleRescheduleAppointment,
  });

  const [buttonText, setButtonText] = useState<"Add More" | "Close">(
    "Add More"
  );
  const showSnackbar = useSnackbar();
  const [openDateCalendar, setOpenDateCalendar] = useState<boolean>(false);
  const [selectedDateTime, setSelectedDateTime] = useState<dayjs.Dayjs | null>(
    null
  );

  function toggleDateCalendar() {
    setOpenDateCalendar(!openDateCalendar);
  }
  function handleRescheduleAppointment(values?: any) {
    const customForms = formik.values?.customForms?.length
      ? formik.values?.customForms?.map((it: any) => it?.value)?.join(",")
      : undefined;

    const assessments =
      (formik.values?.assessments as any)?.length > 0
        ? (formik.values?.assessments as any)
            ?.map((it: any) => it?.value)
            ?.join(",")
        : undefined;

    const transformedValues = { ...values, customForms, assessments };

    rescheduleAppointment({
      rescheduleAppointment: transformedValues,
      xTENANTID: getStoredRealm() ?? undefined,
    })
      .then((res) => {
        closeAndResetForm();
        client.invalidateQueries({
          queryKey: ["appointment-list"],
        });
        client.invalidateQueries({ queryKey: ["future-appointment"] });

        successHandler(getSuccessSnackbar(res)?.message);
      })
      .catch((err) => showSnackbar(getErrorSnackbar(err)));
  }

  function closeAndResetForm() {
    formik.resetForm();
    onClose();
  }

  const nativeDate = dayjs()?.toDate();

  const datePlus3Minutes = nativeDate
    ? new Date(nativeDate.setMinutes(nativeDate.getMinutes() + 3))
    : undefined;

  useEffect(() => {
    formik.setValues(appointmentData);
    setSelectedDateTime(dayjs(datePlus3Minutes));

    setOpenDateCalendar(!!appointmentData?.startTime);

    const assessments =
      appointmentData?.assessments?.length > 0
        ? appointmentData?.assessments?.split(",")?.map((it: any) => ({
            value: it,
            label: replaceUnderScoreAddSpace(it),
          }))
        : undefined;

    setButtonText(
      assessments?.length > 0 || appointmentData?.customForms?.length > 0
        ? "Add More"
        : "Close"
    );

    formik.setFieldValue("assessments", assessments);

    formik.setFieldValue(
      "customForms",
      appointmentData?.customForms?.split(",")?.map((it: any) => ({
        value: it,
        label: INTAKE_FORM_SECTIONS_MAPPER[it],
      }))
    );
    formik.setFieldValue(
      "startTime",
      new Date(appointmentData?.startTime)?.toISOString()
    );
    formik.setFieldValue(
      "endTime",
      new Date(appointmentData?.endTime)?.toISOString()
    );
  }, [appointmentData]);

  const [searchAssessmentString, setSearchAssessmentString] =
    useDebounce<string>("");
  const [assessmentPagination, setAssessmentPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const { data: assessmentList, isFetching: isAssessmentFetching } =
    useGetAllScreenerType({
      pagination: assessmentPagination,
      xTENANTID: getXtenantId(),
      searchString: searchAssessmentString,
    });

  const assessmentListData = useMemo(() => {
    const fetched =
      assessmentList?.data?.content?.map((item: any) => ({
        value: item,
        label: replaceUnderScoreAddSpace(item),
      })) ?? [];
    const selected = (formik.values.assessments ?? []) as {
      [k: string]: string;
    }[];
    const idKey = Object.keys(fetched[0] ?? selected[0] ?? {})[0];
    const missing = selected.filter(
      (s) => !fetched.some((o) => o[idKey as keyof typeof o] === s[idKey])
    );
    return [...missing, ...fetched];
  }, [assessmentList?.data?.content, formik.values.assessments]);

  const isTouched = useRef<boolean>(false);
  return (
    <SideSheetDrawer
      open={opened}
      title="Reschedule Appointment"
      onClose={closeAndResetForm}
      w="800px"
      footerActions={
        <>
          <Button
            size="medium"
            variant="contained"
            onClick={() => formik.handleSubmit()}
          >
            Save And Close
          </Button>
        </>
      }
    >
      <Grid container spacing={3}>
        <Grid item xs={6}>
          <ProfileViewer
            gap={"16px"}
            withoutColn
            firstColW={4.5}
            secondColW={7.5}
            data={{
              patient_name: appointmentData?.patientName,
              Speciality: appointmentData?.providerSpecialities,
              appointment_type: appointmentData?.type ?? "",
              visit_type:
                NEW_APPOINTMENT_VISIT_TYPE_MAPPER[appointmentData?.mode] || "",
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <ProfileViewer
            gap={"16px"}
            withoutColn
            firstColW={4.5}
            secondColW={7.5}
            data={{
              provider_name: appointmentData?.providerName,
              appointment_status:
                APPOINTMENT_STATUS_RECORD[appointmentData?.status as Status],
              patient_pay_type: "",
              // authorization: "",
              current_slot: appointmentData?.startTime
                ? `${formatDateTime(appointmentData?.startTime)}- ${formatTime(appointmentData?.endTime)}`
                : "",
            }}
          />
        </Grid>
        {/* In the Visit type field, if the user has selected 'In Person' visit type, then only this field will be displayed */}
        {formik.values.mode === "IN_PERSON" && (
          <Grid item xs={12}>
            <CustomSelectScroll
              label="Location"
              options={providerGroupLocationList?.data?.content?.map((it) => ({
                value: it?.uuid,
                label: it?.name,
              }))}
              placeholder="Select Location"
              value={formik.values.locationId}
              onChange={(value) => {
                formik.setFieldValue("locationId", value);
              }}
              onSearch={(_, val) => {
                setLocationSearch(val);
                setLocationPagination((p) => ({ ...p, page: 0 }));
              }}
              pagination={locationPagination}
              setPagination={setLocationPagination}
              totalElements={
                providerGroupLocationList?.data?.page?.totalElements
              }
              loading={isLocationFetching}
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <TimeSlotPicker
            isTouched={isTouched}
            formik={formik}
            bookedSlot={appointmentData?.startTime}
            providerUuid={appointmentData?.providerId}
            appointmentType={appointmentData?.appointmentTypeId ?? ""}
            selectedLocation={
              appointmentData?.mode === "VIRTUAL"
                ? undefined
                : appointmentData?.locationId
            }
            mode={appointmentData?.mode}
            isRescheduled={true}
            // label="Date & Time"
            selectedDateTime={selectedDateTime}
            onChange={(value) => {
              setSelectedDateTime(value as dayjs.Dayjs);
              formik.setFieldValue("date_and_time", value?.toISOString());
            }}
            openDateCalendar={openDateCalendar}
            toggleDateCalendar={toggleDateCalendar}
            type="rescheduled"
          />
        </Grid>

        <Grid item sm={12} md={12} lg={12}>
          <FormControlLabel
            name="reminder_set"
            control={<Checkbox />}
            label={"Set Reminder"}
            checked={formik.values.reminder_set}
            onChange={() => {
              formik.setFieldValue("reminder_set", !formik.values.reminder_set);
            }}
          />
        </Grid>
        {formik.values.reminder_set && (
          <Grid item sm={12}>
            <Grid container spacing={1}>
              <Grid item sm={6}>
                <MultiSelect
                  label="Reminder Mediums"
                  options={[
                    { value: "email", label: "E-Mail" },
                    { value: "sms", label: "SMS" },
                    {
                      value: "push_notification",
                      label: "Push Notification",
                    },
                  ]}
                  value={formik.values.reminder_mediums}
                  onChange={(values) => {
                    formik.setFieldValue("reminder_mediums", values);
                  }}
                  // error={
                  //   formik.touched.reminder_mediums &&
                  //   formik.errors.reminder_mediums &&
                  //   `${formik.errors.reminder_mediums}`
                  // }
                />
              </Grid>
              <Grid item sm={2}>
                <NumberInput
                  textFieldProps={{
                    value: formik.values.reminder_before_number,
                    onChange: (e) => {
                      formik.setFieldValue(
                        "reminder_before_number",
                        e.currentTarget.value
                      );
                    },
                  }}
                  label="Remind Before"
                  // value={formik.values.reminder_before_number}
                  // min={1}
                  // onChange={(value) => {
                  //   formik.setFieldValue("reminder_before_number", value);
                  // }}
                  // error={
                  //   formik.touched.reminder_before_number &&
                  //   formik.errors.reminder_before_number &&
                  //   `${formik.errors.reminder_before_number}`
                  // }
                />
              </Grid>
              <Grid item sm={2}>
                <CustomSelect
                  label=" "
                  options={[
                    { value: "minutes", label: "Minutes" },
                    { value: "hours", label: "Hours" },
                    { value: "days", label: "Days" },
                  ]}
                  clearable={false}
                  value={formik.values.reminder_before_unit}
                  onChange={(value) => {
                    formik.setFieldValue("reminder_before_unit", value);
                  }}
                  // error={
                  //   formik.touched.reminder_before_unit &&
                  //   formik.errors.reminder_before_unit &&
                  //   `${formik.errors.reminder_before_unit}`
                  // }
                />
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>
      <Grid container spacing={3} mt={0.5}>
        <Grid item xs={12}>
          <Input
            label="Chief Complaint"
            required
            textFieldProps={{
              placeholder: "Chief Complaint",
              onChange: formik.handleChange,
              onBlur: formik.handleBlur,
              name: "chiefComplaint",
              value: formik.values.chiefComplaint,
            }}
            error={
              formik.touched?.chiefComplaint &&
              (formik.errors?.chiefComplaint as any)
            }
          />
        </Grid>
        <Grid item xs={12}>
          <CustomTextArea
            minRows={3}
            label="Patient Appointment Note"
            placeholder="Appointment Note"
            onChange={formik.handleChange}
            name={"note"}
            value={formik.values.note}
          />
        </Grid>

        <Grid item xs={10}>
          <Flex alignItems={"center"}>
            <FormControlLabel
              control={<Checkbox />}
              label={
                <Text fontWeight={500}>
                  <DescriptionOutlinedIcon />
                  Forms will be emailed
                </Text>
              }
              checked={buttonText === "Add More"}
              onChange={(_, v) => {
                formik.setFieldValue("sendForms", v);
                setButtonText((prev) =>
                  prev === "Add More" ? "Close" : "Add More"
                );
              }}
            />
          </Flex>
        </Grid>

        {buttonText === "Add More" && (
          <Grid item xs={6} container alignContent="flex-start">
            <Grid item xs={12}>
              <MaskMultiSelect
                label="Intake Forms"
                options={INTAKE_FORM_SECTIONS}
                optionWithCheckbox={true}
                placeholder="Search and Select Forms"
                value={formik.values.customForms}
                onChange={(values) => {
                  formik.setFieldValue("customForms", values);
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <ul
                style={{
                  listStyleType: "square",
                }}
              >
                {(formik.values?.customForms ?? [])?.map((f: any) => (
                  <li key={f?.value}>
                    <Text color={"grey"}>{f?.label}</Text>
                  </li>
                ))}
              </ul>
            </Grid>
          </Grid>
        )}

        {buttonText === "Add More" && (
          <Grid item xs={6} container alignContent="flex-start">
            <Grid item xs={12}>
              {buttonText === "Add More" && (
                <MaskMultiSelectScroll
                  label="Assessments"
                  onSearch={(value) => setSearchAssessmentString(value)}
                  options={assessmentListData}
                  optionWithCheckbox={true}
                  placeholder="Search and Select Forms"
                  value={formik.values.assessments}
                  onChange={(values) =>
                    formik.setFieldValue("assessments", values)
                  }
                  pagination={assessmentPagination}
                  setPagination={setAssessmentPagination}
                  totalElements={assessmentList?.data?.page?.totalElements}
                  loading={isAssessmentFetching}
                />
              )}
            </Grid>
            <Grid item xs={12}>
              <ul
                style={{
                  listStyleType: "square",
                }}
              >
                {((formik?.values?.assessments as any) ?? [])?.map((f: any) => (
                  <li>
                    <Text color={"grey"}>{f?.label}</Text>
                  </li>
                ))}
              </ul>
            </Grid>
          </Grid>
        )}
      </Grid>
    </SideSheetDrawer>
  );
};

export default RescheduleAppointment;
